import React, {useMemo} from 'react';
import CircleProgress from "../../components/CircleProgress";
import User from "../../models/User";
import {Link, useLocation} from "react-router-dom";
import {Img} from "react-image";
import LoadingSpinner from "../../components/LoadingSpinner";
import UserImageCircle from "../../components/UserImageCircle";
import InfoTooltip from "../../components/InfoTooltip";
import TeamMatchDetails from "./TeamMatchDetails";
import {useTeamCandidate} from "../../providers/TeamCandidateProvider";
import {useAuth} from "../../providers/AuthProvider";
import {Badge as AntdBadge, Dropdown} from "antd";

const TeamCandidateHorizontalTile = (props) => {
    const { team, canFavorite, canArchive, criteria, removeFromFavorites, addToFavorites, addToLiked, removeFromLiked, addToArchive, removeFromArchive } = useTeamCandidate();
    const location = useLocation();

    const {auth} = useAuth();
    const vips = TB.filter(team.topPositionsForDisplay(), vip => TB.isDefined(vip)).map(vip => User.make({ ...vip }));


    const teamMemberLink = useMemo(() => {
        const params = { team_id: team.id }

        const state = {
            linkParams: {
                title: 'Back',
                path: location.pathname + (location.search || '')
            }
        }
        return { state, params }
    }, [team, location.pathname]);

    const teamDetailsLink = useMemo(() => {
        if (!location.pathname) {
            return ''
        }
        const params = { team_id: team.id }

        const state = {
            linkParams: {
                path: location.pathname + (location.search || '')
            }
        }

        return {
            ...location,
            pathname: `team-profiles/${team.id}`,
            state
        }
    }, [team, location.pathname]);

    const showMatchDetails = () => {
        let matchCriteria = {};
        if (criteria) {
            matchCriteria = criteria;
        }

        return TB.event('Modal@show', {
            modalClass: 'match-details-modal',
            content: (
                <TeamMatchDetails
                    team={team}
                    isEngagementLead={false}
                    criteria={matchCriteria}
                />
            )
        })
    }

    const tooltipId = `${team?.id}-browse`
    const favoriteId = "fav-btn-" + tooltipId;
    const archiveId = "arch-btn-" + tooltipId;


    const userLikes = team.job_candidate?.likes || [];
    const otherUserLikes = TB.filter(userLikes, like => like.causer?.id !== auth.id);

    return (
        <div className="team-horizontal-tile p-4 pb-0 mb-8 bg-wrippleOffWhite">

            <div className="bg-white w-full">
                <div className="p-4 md:flex justify-between md:space-x-12 border-b border-[#CECECE]">

                    <div className="w-full md:w-1/3 flex justify-between md:pb-0 pb-4">
                        <div className="flex flex-row items-start">
                            <div className="flex flex-row items-center h-8 justify-start w-[30px]">
                                {canArchive && (
                                    <React.Fragment>
                                        <button
                                            className="btn archive-btn pl-0"
                                            style={styles.archiveBtn}
                                            onClick={() => {
                                                props.onRequest(
                                                    team.wasDisliked() ?
                                                        removeFromArchive :
                                                        addToArchive
                                                )
                                            }}
                                            id={archiveId}
                                            disabled={props.isDisabled}>
                                            <i className={team.wasDisliked() ? "fas fa-plus-circle" : "fas fa-times-circle"}/>
                                        </button>
                                        <InfoTooltip key={archiveId + 'tooltip'} type={"dark"} target={archiveId}>
                                            {team.wasDisliked() ? "Remove from archive" : "Archive"}
                                        </InfoTooltip>

                                    </React.Fragment>
                                )}
                            </div>
                            <div>
                                <p className="text-3xl font-bold">{team.name}</p>
                                {(team.type === 'Agency' && team.supplier_client) ? (
                                    <Link className="text-primary flex items-center gap-2 text-xs md:text-sm hover:underline" to={`/agency-profiles/${team.supplier_client.id}`}>
                                        {team.type}
                                        <img className="h-7" src={team.supplier_client.logo_path} />
                                    </Link>
                                ) : (
                                    <p>{team.type}</p>
                                )}
                            </div>
                        </div>

                        <div className="md:hidden">
                            {canFavorite && (
                                <div className="flex flex-row items-center h-8 justify-end w-[65px] ml-auto">
                                    <AntdBadge count={otherUserLikes?.length ? userLikes.length : 0} size="large" className="ml-auto mr-[-2px]">
                                        <button
                                            className="btn like-btn"
                                            style={styles.favoriteBtn}
                                            onClick={() => {
                                                props.onRequest(
                                                    team.wasLiked() ?
                                                        removeFromLiked :
                                                        addToLiked
                                                )
                                            }}
                                            id={favoriteId}
                                            disabled={props.isDisabled}>
                                            <i className={TB.mapClass("fa-thumbs-up", {
                                                "fas": team.wasLiked(),
                                                "far": !team.wasLiked(),
                                                "text-success": !!userLikes.length
                                            })}/>

                                        </button>
                                    </AntdBadge>


                                    <InfoTooltip type="dark" placement={!!otherUserLikes?.length ? 'bottom' : 'top'}
                                                 target={favoriteId}>
                                        <div className="px-0 py-1.5 text-left flex flex-col items-start gap-1">
                                            {!!otherUserLikes?.length ? (
                                                TB.orderBy(
                                                    TB.map(userLikes, like => {
                                                        if (!like.causer) return null;

                                                        return (
                                                            <p className="text-xxs font-medium p-0 text-white gap-1 flex flex-row items-center"
                                                               key={like.id}>
                                                                <span>{`${like.causer.first_name} ${like.causer.last_name}`}</span>
                                                                {like.causer.id === auth.id && <span className="italic text-red-400">(You)</span>}
                                                                {/*<span className="font-light italic underline">{TB.moment(like.created_at).fromNow()}</span>*/}
                                                            </p>
                                                        )
                                                    }), like => like.causer?.id === auth.id ? 1 : 0, 'desc')
                                            ) : (
                                                <p className="font-medium text-xxs p-0 text-white">
                                                    {team.wasLiked() ? 'Unlike' : 'Like'}
                                                </p>
                                            )}
                                        </div>
                                    </InfoTooltip>
                                    <Dropdown menu={{
                                        items: [{
                                            key: 'favorite',
                                            icon: <i
                                                className={"text-gray mr-2 " + (team.wasDisliked() ? "fas fa-plus-circle" : "fas fa-times-circle")}/>,
                                            label: team.is_favorite ? "Remove from saved" : "Save as Favorite"
                                        }, {
                                            key: 'archive',
                                            icon: <i
                                                className={"text-danger mr-2 fa-heart " + (team.is_favorite ? "fas" : "far")}/>,
                                            label: team.wasDisliked() ? "Remove from archive" : "Archive"
                                        }],
                                        onClick: async ({item, key, keyPath, domEvent}) => {
                                            await TB.wait(200)
                                            switch (key) {
                                                case 'favorite':
                                                    return props.onRequest(
                                                        team.is_favorite ? removeFromFavorites : addToFavorites
                                                    )
                                                case 'archive':
                                                    return props.onRequest(
                                                        team.wasDisliked() ?
                                                            removeFromArchive :
                                                            addToArchive
                                                    )
                                            }
                                        }
                                    }} trigger={['click']} placement="bottomRight">
                                        <i className="fas fa-ellipsis-v ml-2"/>
                                    </Dropdown>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="w-full text-centr md:w-2/3 md:flex">
                        <div className="w-full">

                            <div className="line-clamp-2 mb-2">
                                <div className="text-sm" dangerouslySetInnerHTML={{ __html: team.fullBio() }}>

                                </div>
                            </div>

                            <Link className="details-link text-base" to={teamDetailsLink}>
                                {TB.trans('teams.project_info_panel.detail_link')}
                            </Link>
                        </div>

                        <div className="hidden md:block">
                            {canFavorite && (
                                <div className="flex flex-row items-center h-8 justify-end w-[65px] ml-auto">
                                    <AntdBadge count={otherUserLikes?.length ? userLikes.length : 0} size="large" className="ml-auto mr-[-2px]">
                                        <button
                                            className="btn like-btn"
                                            style={styles.favoriteBtn}
                                            onClick={() => {
                                                props.onRequest(
                                                    team.wasLiked() ?
                                                        removeFromLiked :
                                                        addToLiked
                                                )
                                            }}
                                            id={favoriteId}
                                            disabled={props.isDisabled}>
                                            <i className={TB.mapClass("fa-thumbs-up", {
                                                "fas": team.wasLiked(),
                                                "far": !team.wasLiked(),
                                                "text-success": !!userLikes.length
                                            })}/>

                                        </button>
                                    </AntdBadge>


                                    <InfoTooltip type="dark" placement={!!otherUserLikes?.length ? 'bottom' : 'top'}
                                                 target={favoriteId}>
                                        <div className="px-0 py-1.5 text-left flex flex-col items-start gap-1">
                                            {!!otherUserLikes?.length ? (
                                                TB.orderBy(
                                                    TB.map(userLikes, like => {
                                                        if (!like.causer) return null;

                                                        return (
                                                            <p className="text-xxs font-medium p-0 text-white gap-1 flex flex-row items-center"
                                                               key={like.id}>
                                                                <span>{`${like.causer.first_name} ${like.causer.last_name}`}</span>
                                                                {like.causer.id === auth.id && <span className="italic text-red-400">(You)</span>}
                                                                {/*<span className="font-light italic underline">{TB.moment(like.created_at).fromNow()}</span>*/}
                                                            </p>
                                                        )
                                                    }), like => like.causer?.id === auth.id ? 1 : 0, 'desc')
                                            ) : (
                                                <p className="font-medium text-xxs p-0 text-white">
                                                    {team.wasLiked() ? 'Unlike' : 'Like'}
                                                </p>
                                            )}
                                        </div>
                                    </InfoTooltip>
                                    <Dropdown menu={{
                                        items: [{
                                            key: 'favorite',
                                            icon: <i
                                                className={"text-danger mr-2 fa-heart " + (team.is_favorite ? "fas" : "far")}/>,
                                            label: team.is_favorite ? "Remove from saved" : "Save as Favorite"
                                        }],
                                        onClick: async ({item, key, keyPath, domEvent}) => {
                                            await TB.wait(200)
                                            props.onRequest(
                                                team.is_favorite ? removeFromFavorites : addToFavorites
                                            )
                                        }
                                    }} trigger={['click']} placement="bottomRight">
                                        <i className="fas fa-ellipsis-v ml-2"/>
                                    </Dropdown>
                                </div>
                            )}
                        </div>

                    </div>

                </div>

                <div className="p-4 md:flex justify-between md:space-x-12">

                    <div className="w-full md:w-1/3 md:border-r pb-3 border-[#CECECE] flex md:flex-col justify-center gap-4">
                        <div className="h-full flex justify-center">
                            <CircleProgress
                                className="metric-circle self-center w-40"
                                progress={team.overallMatchPercent()}
                                strokeWidth={7}
                            >
                                <p>
                                    <span className="text-2xl">{team.overallMatchPercent(true)}%</span>
                                    <small className="text-base">Match</small>
                                </p>
                            </CircleProgress>
                        </div>

                        <button type="button" className="details-link" onClick={showMatchDetails}>More</button>
                    </div>

                    <div className="w-full md:w-2/3 pt-6 md:pt-0">
                        <div className="flex gap-3 justify-btween items-center">
                            <p className="text-xl text-primary">Team Members</p>
                            <Link className="details-link text-xs md:text-sm" to={teamDetailsLink}>
                                View all {team.positions?.length} members
                            </Link>
                        </div>


                        {/* FOR MOBILE */}
                        <div className="pt-4 flex flex-col md:hidden divide-y">
                            {vips.map((user, i) => {
                                const filteredDisciplines = user.disciplines.filter(d => !!d.name);
                                return (
                                    <div key={`${user.id}-${team.id}`} className="flex flex-1 gap-6 py-3 items-center">

                                        <div className="flex-shrink-0">

                                            <Link to={{
                                                ...location,
                                                pathname: `${location.pathname}/team-profiles/${team.id}/talent-profiles/${user.id}`,
                                                state: teamMemberLink.state
                                            }} className="flex-shrink-0">
                                                <Img
                                                    loader={(
                                                        <div className="justify-center h-20 w-20 items-center flex flex-col">
                                                            <LoadingSpinner />
                                                        </div>
                                                    )}
                                                    unloader={<UserImageCircle user={user} hideTooltip={true} size={60} />}
                                                    src={user.image_path}
                                                    className="img-grayscale img-fluid h-20 w-20 rounded-full " />
                                            </Link>

                                            <Link className="details-link block text-center pt-2 text-xs md:text-sm" to={{
                                                ...location,
                                                pathname: `${location.pathname}/team-profiles/${team.id}/talent-profiles/${user.id}`,
                                                state: teamMemberLink.state
                                            }}>
                                                View Profile
                                            </Link>
                                        </div>

                                        <div className="flex">
                                            <p className={"member-name"}>{user.firstNamePlusInitial()}</p>
                                            <p className="line-clamp-1 w-full md:w-3/4 text-xxs md:text-center">
                                                {user.job_title}
                                            </p>

                                            <div className="py-2 matched-roles-teams">
                                                <div className="flex gap-2 items-center flex-wrap">
                                                    {filteredDisciplines.slice(0, 2)?.map(item => {
                                                        return (
                                                            <p className={TB.mapClass("role2 text-center", {
                                                                'max-w-[260px]': filteredDisciplines?.length === 1,
                                                                'max-w-[150px]': filteredDisciplines?.length === 2
                                                            })} key={`r-${item.id}`}>
                                                                {item.name}
                                                            </p>
                                                        )
                                                    })}
                                                </div>
                                            </div>

                                            {filteredDisciplines?.length > 2 && (
                                                <div>
                                                    <p id={`matched-roles-${team.id}-${user.id}`} className="more-roles text-xs md:text-xxs">
                                                        + {TB.pluralize('role', filteredDisciplines?.length - 2, true)}
                                                    </p>

                                                    <InfoTooltip
                                                        type="dark"
                                                        placement="top"
                                                        target={`matched-roles-${team.id}-${user.id}`}
                                                    >
                                                        {filteredDisciplines.slice(2).map(discipline => (
                                                            <p key={discipline.id} className="px-3 py-1 m-0 text-white text-left">
                                                                {discipline.name}
                                                            </p>
                                                        ))}
                                                    </InfoTooltip>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        {/* FOR DESKTOP */}
                        <div className="hidden md:flex pt-4 justify-between gap-6">
                            {vips.map((user, i) => {
                                const filteredDisciplines = user.disciplines.filter(d => !!d.name);
                                return (
                                    <div key={`${user.id}-${team.id}`} className="flex flex-col items-center bg-wrippleOffWhite w-full px-2 py-4">
                                        <Link to={{
                                            ...location,
                                            pathname: `${location.pathname}/team-profiles/${team.id}/talent-profiles/${user.id}`,
                                            state: teamMemberLink.state
                                        }}>
                                            {/* <Img
                                                loader={(
                                                    <div className="justify-center h-20 w-20 items-center flex flex-col">
                                                        <LoadingSpinner />
                                                    </div>
                                                )}
                                                unloader={<UserImageCircle user={user} hideTooltip={true} size={60} />}
                                                src={user.image_path}
                                                className="img-grayscale img-fluid h-20 w-20 rounded-full" /> */}
                                            <UserImageCircle user={user} hideTooltip={true} size={80} className="img-grayscale img-fluid h-20 w-20 rounded-full" />
                                        </Link>

                                        <p className="member-name pt-1.5">{user.firstNamePlusInitial()}</p>
                                        <p className="line-clamp-1 w-3/4 text-xxs text-center">
                                            {user.job_title}
                                        </p>


                                        <div className="p-2 matched-roles-teams">
                                            <div className="space-y-2">
                                                {filteredDisciplines.slice(0, 2)?.map(item => {
                                                    return (
                                                        <p className={TB.mapClass("role2 text-center", {
                                                            'max-w-[260px]': filteredDisciplines?.length === 1,
                                                            'max-w-[150px]': filteredDisciplines?.length === 2
                                                        })} key={`r-${item.id}`}>
                                                            {item.name}
                                                        </p>
                                                    )
                                                })}
                                            </div>
                                        </div>

                                        {filteredDisciplines?.length > 2 && (
                                            <div>
                                                <p id={`matched-roles-${team.id}-${user.id}`} className="more-roles">
                                                    + {TB.pluralize('role', filteredDisciplines?.length - 2, true)}
                                                </p>

                                                <InfoTooltip
                                                    type="dark"
                                                    placement="top"
                                                    target={`matched-roles-${team.id}-${user.id}`}
                                                >
                                                    {filteredDisciplines.slice(2).map(discipline => (
                                                        <p key={discipline.id} className="px-3 py-1 m-0 text-white text-left">
                                                            {discipline.name}
                                                        </p>
                                                    ))}
                                                </InfoTooltip>
                                            </div>
                                        )}

                                        <Link className="details-link text-sm pt-2 mt-auto" to={{
                                            ...location,
                                            pathname: `${location.pathname}/team-profiles/${team.id}/talent-profiles/${user.id}`,
                                            state: teamMemberLink.state
                                        }}>
                                            View Profile
                                        </Link>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>

                <div className="bg-wrippleOffWhite p-4 md:flex justify-end md:space-x-12">
                    <div className={TB.mapClass('candidate-panel w-auto no-min-width bg-transparent m-0 md:pr-12', {
                        'md:border-r border-[#CECECE]': !props.isBrowse,
                    })}>
                        <div className="candidate-rate-section p-2 bg-transparent">
                            <h4>Average Standard Rate</h4>
                            <h2 className="currency-sup">
                                {team.rangedPlatformRates()}
                                <span>/hr</span>
                            </h2>
                        </div>
                    </div>

                    {!props.isBrowse && (
                        <div className="flex items-center justify-center pt-4 md:pt-0 md:min-w-[30%]">
                            {props.children || (
                                <button className="btn btn-primary btn-block request-tn text-base min-w-[250px] w-full">
                                    Invite to Project
                                </button>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    )
}

const styles = {
    favoriteBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-end'
    },

    archiveBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-start'
    },
    inviteLockIcon: {
        position: 'absolute',
        fontSize: 13,
        right: -18,
    }
};
export default TeamCandidateHorizontalTile;